import { inject, observer } from "mobx-react";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "src/config";
import { MatchParams, getRoutePath, Page, RoomView, LogView } from "src/routes";
import { AuthStore, RoutingStore, SpaceNode, SpaceStore } from "src/stores";
import { SpaceType } from "src/types";
import { Space } from "src/models";
import styled from "styled-components";
import { Icon, Logo } from "..";
import { match } from "react-router";
import { LanguageSelect } from "./languageMenu";
import { RoomSidebarRow } from "./roomSidebarRow";
import { Chevron } from "./chevron";
import { sortAlph } from "src/util/util";
import { Sidebar } from "../sidebar";

export type Row = {
  action?:       ReactNode;
  active?:       boolean;
  children?:     Row[];
  divider?:      boolean;
  hide?:         boolean;
  icon?:         ReactNode;
  key?:          string;     // If this is not provided, the label will be used instead
  label?:        string;
  parent_id?:    number;
  project_id?:   number;
  space_id?:     number;
  style?:        any;
  disabled?:     boolean;
  subItemDepth?: number;
  tooltip?:      string;
  onClick?:      () => void;
  onAction?:     () => void;
};

export interface IRoomSidebarProps {
  authStore?:    AuthStore;
  children?:     ReactNode;
  routingStore?: RoutingStore;
  spaceStore?:   SpaceStore;
  match?:        match<MatchParams>;  // This is injected so it has to be called match
}


export const RoomSidebar = inject("authStore", "spaceStore", "routingStore")(observer((props: IRoomSidebarProps): JSX.Element => {
  const { match, authStore, children, routingStore, spaceStore } = props;
  const { t, i18n } = useTranslation();

  const [showRooms,    setShowRooms]    = React.useState<boolean>(false);
  const [showProjects, setShowProjects] = React.useState<boolean>(false);
  const toggleProjects = React.useCallback(() => setShowProjects(!showProjects), [showProjects]);
  const toggleRooms    = React.useCallback(() => setShowRooms(!showRooms), [showRooms]);

  const curRoom: Space = authStore.getCurRoomID
  ? spaceStore.getItemByID(authStore.getCurRoomID)
  : null;

  const curProject: Space = authStore.getCurProjectID
    ? spaceStore.getItemByID(authStore.getCurProjectID)
    : null;

  // The spaceStore only loads the projects of the current workspace
  const projects:    Space[]     = spaceStore.getUserSpaces(SpaceType.Project);
  const room_forest: SpaceNode[] = spaceStore.getRoomForest;

  const footer = (
    <FooterWrapper>
      <LanguageSelect />
    </FooterWrapper>
  );

  const header = (
    <Header>
      <LogoWrapper>
        <Logo width="15rem" size={"normal"} />
      </LogoWrapper>
    </Header>
  );

  const getRoomNode = (room: Space): SpaceNode => {
    const findRoomInChildren = (node:SpaceNode, space: Space): SpaceNode => {
      for(const c of node.children) {
        if(c.space.id === space.id) return c;
        const inChildren = findRoomInChildren(c, room);
        if(inChildren) return inChildren;
      }
      return null;
    }
    for(const node of room_forest) {
      if(node.space.id === room.id) return node;
      const inChildren = findRoomInChildren(node, room);
      if(inChildren) return inChildren;
    }
    return null;
  }

  const getRoomChildrenRows = (children: SpaceNode[], hide: boolean): Row[] => (
    children?.map(c => ({
      active:   c.space.id === curRoom?.id,
      hide:     hide,
      key:      "r_"+c.space.id,
      label:    c.space.title,
      children: getRoomChildrenRows(c.children, hide),
      onClick:  () => {
        spaceStore.changeRoom(c.space.id, true);
      },
    })) || undefined
  );
  const rows: Row[] = [
    {
      // Home Section
      active:  match.path === "/" && !showProjects && !showRooms,
      divider: false,
      icon:    <Icon iconId="home" size="large" color={theme.color.sidebar.icon} />,
      key:     "home",
      label:   t("Start"),
      onClick: () => spaceStore.goToDashboard(),
      style:   { marginBottom: "1rem", fontWeight: 700 },
      tooltip: t("home"),
    },{
      // Project Select Header
      action:   <Chevron up={showProjects} />,
      divider:  false,
      key:      "select_project",
      label:    t("project"),
      onAction: toggleProjects,
      onClick:  toggleProjects,
    },{
      // Current Project
      divider:  false,
      hide:     showProjects,
      key:      "current_project",
      label:    curProject?.title || t("no_project_selected"),
      onClick: () => curProject ? routingStore.push(getRoutePath(Page.ProjectSettings, {id:curProject.id, view: 'settings'})): {},
    },
    // Project list
    ...projects.map<Row>(p => ({
      active:  p.id === curProject?.id,
      divider: false,
      hide:    !showProjects,
      key:     "p_"+p.id,
      label:   p.title,
      onClick: async () => {
        await spaceStore.changeProject(p.id);
        setShowProjects(false);
        setShowRooms(true);
      },
    })),
    { // Placeholder if there are no projects to select
      divider: false,
      hide:    !showProjects || Boolean(projects?.length),
      key:      "project_placeholder",
      label:   t("no_projects_to_select"),
    },{
      key: "divider",
      divider: true,
    },{
      // Room Select Header
      hide:     !curProject,
      action:   <Chevron up={showRooms} />,
      divider:  false,
      key:      "select_room",
      label:    t("meeting_room"),
      onAction: toggleRooms,
      onClick:  toggleRooms,
    },
    // Room List
    ...room_forest.map(node => ({
      active:   node.space.id === curRoom?.id,
      children: getRoomChildrenRows(node.children, !showRooms),
      hide:     !showRooms,
      key:     "r_"+node.space.id,
      label:    node.space.title,
      onClick:  async () => {
        await spaceStore.changeRoom(node.space.id, true);
        setShowRooms(false);
      },
    })),
      ...(!!curRoom ? curRoom.getRoomSubTree.map<Row>(r => ({
        active:       r.id === curRoom?.id,
        children:     (r.id === curRoom?.id) ? getRoomChildrenRows(getRoomNode(r)?.children || [], !curRoom) : [],
        divider:      false,
        hide:         showRooms,
        key:          "cur_r_"+r.id,
        label:        r.title,
        subItemDepth: r.getRoomDepth,
        onClick:      () => spaceStore.changeRoom(r.id, true),
      })) : []),
    {
      // Current Room placeholder
      divider:  false,
      hide:     true,
      key:      "current_room_placeholder",
      label:    t("no_room_selected"),
    },{ // Placeholder if there are no rooms to select
      divider: false,
      hide:    true || !showRooms || Boolean(room_forest?.length),
      key:     "room_placeholder",
      label:   t("no_rooms_to_select"),
    },{
      active:  match?.params?.view === "deliverable",
      hide:    !authStore.getCurRoomID,
      icon:    <Icon iconId="watch" color={theme.color.sidebar.icon} size="large" />,
      label:   t("deliverable_plural"),
      key:     "delivs",
      onClick: () => routingStore.push(getRoutePath(Page.Room, {id:curRoom.id, view: RoomView.Deliverable})),
      style:   { marginTop: "2rem" },
      tooltip: t("deliverable_plural"),
    },{
      active:  match?.params?.view === "question",
      hide:    !authStore.getCurRoomID,
      icon:    <Icon iconId="questionAnswerIcon" color={theme.color.sidebar.icon} size="large" />,
      label:   t("question_plural"),
      key:     "questions",
      onClick: () => routingStore.push(getRoutePath(Page.Room, { id: curRoom.id, view: RoomView.Question })),
      tooltip: t("question_plural"),
    },{
      active:  match?.params?.view === "meeting",
      hide:    !authStore.getCurRoomID,
      icon:    <Icon iconId="meeting" color={"sidebarIcon"} size="medium" />,
      key:     "meetings",
      label:   t("meeting_plural"),
      onClick: () => routingStore.push(getRoutePath(Page.Room, { id: curRoom.id, view: RoomView.Meeting })),
      tooltip: t("meeting_plural"),
    },{
      active:  match?.params?.view?.includes('log'),
      hide:    !authStore.getCurRoomID,
      icon:    <Icon iconId="list" color={"sidebarIcon"} size="large" />,
      key:     "log",
      label:   t("log_plural"),
      onClick: () => routingStore.push(getRoutePath(Page.RoomLog, {id: authStore.getCurRoomID, view: "logs", type: LogView.Question})),
      tooltip: t("log_plural"),
    },{
      active:  match?.params?.view?.includes('documents'),
      hide:    !authStore.getCurRoomID,
      icon:    <Icon iconId="document" color={"sidebarIcon"} size="large" />,
      key:     "documents",
      label:   t("documents"),
      onClick: () => routingStore.push(getRoutePath(Page.Documents, { id: authStore.getCurRoomID, view: "documents" })),
      tooltip: t("documents"),
    },{
      active:  match.path === "/room/:room_id/archive",
      hide:    !authStore.getCurRoomID || !authStore.user.canEdit,
      icon:    <Icon iconId="archive" size="large" color={theme.color.sidebar.icon} />,
      key:     "archive",
      label:   t("archive_page"),
      onClick: () => routingStore.push(getRoutePath(Page.Room, {id: authStore.getCurRoomID, view: "archive"})),
      tooltip: t("archive_page"),
    },
    {
      active:  match.path === "/room/:room_id/settings",
      hide:    !authStore.getCurRoomID || !authStore.user.canEdit,
      icon:    <Icon iconId="settings" size="large" color={theme.color.sidebar.icon} />,
      key:     "settings",
      label:   t("settings"),
      onClick: () => routingStore.push(getRoutePath(Page.RoomSettings, {id: authStore.getCurRoomID, view: "settings"})),
      tooltip: t("settings"),
    },
  ];

  const content =  spaceStore.isLoading
    ?  <></>
    : (
      <ContentContainer>
        <ScrollContainer>
          {rows.filter(Boolean).map((r, index) => (
            <RoomSidebarRow key={r.key} row={r} />
          ))}
        </ScrollContainer>
      </ContentContainer>
    );

  return (
    <Sidebar
      type    = {"static"}
      size    = {"small"}
      header  = {header}
      footer  = {footer}
      content = {content}
    >
      {React.Children.map(children, (c) => c)}
    </Sidebar>
  );
}));

const ScrollContainer = styled.ul`
  overflow-y: auto;
`;

const ContentContainer = styled.div`
  background-color: black;
  height:           100%;
  max-height:       100%;
  overflow-x:       hidden;
  overflow-y:       auto;
  padding-left:     0;
  position:         absolute;
  width:            100%;
`;

const FooterWrapper = styled.div`
  display:          flex;
  background-color: ${theme.color.sidebar.background};
`;

const LogoWrapper = styled.div`
  align-items:    flex-end;
  display:        flex;
  cursor:         pointer;
  flex-direction: column;
`;

const Header = styled.div`
  display:          flex;
  align-items:      center;
  justify-content:  center;
  width:            100%;
  background-color: black;
  color:            ${theme.color.sidebar.icon};
  padding:          0.75rem 0.5rem 0.75rem 2.25rem;
  min-height:       6.25rem;
`;